/**
 * verifyPasskeySignIn — turns a passkey assertion into a session.
 *
 * The caller is unauthenticated, so nothing it sends names the account: the
 * door hashes `credentialId`, opens the stored credential and takes the uid
 * from it. `userHandle` is diagnostic only — logged when it disagrees, never a
 * rejection. There is no `isNewUser`: a passkey never creates an account, and
 * the door refuses when the product's `assertAccount` no longer admits the
 * stored uid.
 *
 * Fields justified:
 *   credentialId      — hashed, the stored credential's address.
 *   authenticatorData — the signed statement: the relying-party id hash and
 *                       the user-verification flag; the first half of what
 *                       the signature covers.
 *   clientDataJSON    — origin, type pinned to the assertion string, and the
 *                       challenge; its sha256 is the second half of what the
 *                       signature covers.
 *   signature         — over the two above, verified against the stored key.
 *   userHandle        — diagnostic; nullish because a field that gates nothing
 *                       must not fail a sign-in at the parse seam.
 * Output:
 *   token             — Firebase custom token, consumed by
 *                       `signInWithCustomToken` and never stored. The object is
 *                       loose: the product's `mintSession` may answer more
 *                       beside it, and that passes through unchanged.
 * Excluded:
 *   isNewUser / uid / email — resolved server-side; none crosses the wire.
 */
import { z } from 'zod';
import { base64url } from './base64url.ts';

export const VerifyPasskeySignInInput = z
  .object({
    credentialId: base64url.describe(
      "the authenticator's own id for the credential that signed, as base64url",
    ),
    authenticatorData: base64url.describe(
      'the authenticator data the credential signed over, carrying the relying-party id hash and the user-verification flag, as base64url',
    ),
    clientDataJSON: base64url.describe(
      "the browser's own record of the ceremony — origin, type and challenge — as base64url",
    ),
    signature: base64url.describe(
      'the assertion signature over the authenticator data and the hash of the client data, as base64url',
    ),
    userHandle: base64url
      .nullish()
      .describe(
        'the account id the authenticator holds for this credential, as base64url; recorded for diagnosis and never used to decide whose credential this is',
      ),
  })
  .strict();
export type VerifyPasskeySignInInput = z.infer<typeof VerifyPasskeySignInInput>;

export const VerifyPasskeySignInOutput = z.looseObject({
  token: z.string().min(1).describe('Firebase custom token for the authenticated session'),
});
export type VerifyPasskeySignInOutput = z.infer<typeof VerifyPasskeySignInOutput>;
